/** @typedef {Window & { openCookieWidgetPopup?: () => void }} ConsentMoWindow */

// Seed list for first-time "Accept & watch" — update if merchant adds non-marketing optional categories in ConsentMo admin.
const NON_MARKETING_OPTIONAL_CATEGORIES = ['analytics', 'functionality'];

// Keep #hasMarketingConsent in sync with hasStoredMarketingConsent in snippets/klaviyo-consent.liquid.
class YouTubeConsent extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {string | null} */
  #placeholderHTML = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (this.#placeholderHTML == null) {
      this.#placeholderHTML = this.innerHTML;
    }

    if (!this.#isLoaded()) {
      this.#bindPlaceholderButtons(signal);
    }

    document.addEventListener('consentmoSignal', /** @type {EventListener} */ (this.#onSignal), { signal });
    document.addEventListener('consentmoSignal_onLoad', /** @type {EventListener} */ (this.#onLoad), { signal });

    // Local channel from #acceptMarketing — don't reuse consentmoSignal, its richer payload is consumed by ConsentMo's GTM/CMP listeners.
    document.addEventListener('youtube-consent:marketing-granted', /** @type {EventListener} */ (this.#onLocalGrant), { signal });

    if (this.#hasMarketingConsent()) this.#loadIframe();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * @param {CustomEvent<{ preferences?: { marketing?: boolean } }>} event
   */
  #onSignal = (event) => {
    if (event.detail?.preferences?.marketing) {
      this.#loadIframe();
    } else {
      this.#unloadIframe();
    }
  };

  /**
   * @param {CustomEvent<{ isActive?: boolean }>} event
   */
  #onLoad = (event) => {
    if (!event.detail?.isActive) this.#loadIframe();
  };

  #onLocalGrant = () => {
    this.#loadIframe();
  };

  /**
   * @param {AbortSignal} signal
   */
  #bindPlaceholderButtons(signal) {
    this.querySelector('.youtube-consent__accept-btn')?.addEventListener('click', this.#acceptMarketing, { signal });
    this.querySelector('.youtube-consent__settings-link')?.addEventListener('click', this.#openConsentDialog, {
      signal,
    });
  }

  // Writes ConsentMo's cookies directly; dispatching consentmoSignal would feed GTM/CMP listeners an incomplete payload. ConsentMo's runtime, Shopify Customer Privacy, GCM, and GTM dataLayer re-init on next navigation.
  #acceptMarketing = () => {
    const status = this.#getCookie('cookieconsent_status');
    const existing = this.#getCookie('cookieconsent_preferences_disabled');

    // 'deny' status leaves the disabled list empty — seed with the known list so filtering only 'marketing' doesn't silently enable the rest.
    const baseDisabled =
      status && status !== 'deny'
        ? existing
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : NON_MARKETING_OPTIONAL_CATEGORIES.slice();

    const remaining = baseDisabled.filter((s) => s !== 'marketing').join(',');

    const maxAge = 60 * 60 * 24 * 365;
    const flags = `path=/;max-age=${maxAge};SameSite=Lax;Secure`;
    document.cookie = `cookieconsent_status=accept_selected;${flags}`;
    document.cookie = `cookieconsent_preferences_disabled=${remaining};${flags}`;

    document.dispatchEvent(new CustomEvent('youtube-consent:marketing-granted'));
  };

  /**
   * @param {string} name
   * @returns {string}
   */
  #getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match?.[1] ? decodeURIComponent(match[1]) : '';
  }

  #hasMarketingConsent() {
    const status = this.#getCookie('cookieconsent_status');
    if (!status || status === 'deny') return false;
    const disabled = this.#getCookie('cookieconsent_preferences_disabled');
    return !disabled.split(',').map((s) => s.trim()).includes('marketing');
  }

  #openConsentDialog = () => {
    const win = /** @type {ConsentMoWindow} */ (window);
    if (typeof win.openCookieWidgetPopup !== 'function') return;
    // On first visit ConsentMo's banner is already visible — don't stack a second dialog.
    if (!this.#getCookie('cookieconsent_status')) return;
    win.openCookieWidgetPopup();
  };

  // Iframe presence rather than a flag so reconnects don't restart the flow.
  #isLoaded() {
    return !!this.querySelector('iframe');
  }

  #loadIframe() {
    if (this.#isLoaded()) return;
    if (!this.dataset.videoSrc) return;

    const iframe = document.createElement('iframe');
    iframe.src = this.dataset.videoSrc;
    iframe.title = this.dataset.videoTitle ?? '';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;border:0';

    this.innerHTML = '';
    this.appendChild(iframe);
  }

  #unloadIframe() {
    if (!this.#isLoaded() || this.#placeholderHTML == null || this.#controller == null) return;
    this.innerHTML = this.#placeholderHTML;
    this.#bindPlaceholderButtons(this.#controller.signal);
  }
}

if (!customElements.get('youtube-consent')) {
  customElements.define('youtube-consent', YouTubeConsent);
}
